/**
 * Practice Immunization Template API
 * API utilities for managing practice-specific vaccination protocols
 */

import { API_BASE_URL } from '../config/api';
import {
    PracticeTemplate,
    PracticeTemplateListItem,
    PracticeTemplateItem,
    CreatePracticeTemplate,
    UpdatePracticeTemplate,
    CreatePracticeTemplateItem,
    UpdatePracticeTemplateItem,
    ApplyPracticeTemplateRequest,
    StandardTemplate,
    ImportStandardTemplateRequest,
    ReminderTrigger,
    CreateReminderTrigger,
    UpdateReminderTrigger
} from '../types/practice-immunization-template';
import { ImmunizationSchedule } from '../types/immunization-schedule';

function getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
    };
}

// ===== TEMPLATE CRUD =====

export async function getPracticeTemplates(
    practiceId: string,
    options?: { species?: string; activeOnly?: boolean }
): Promise<PracticeTemplateListItem[]> {
    const params = new URLSearchParams();
    if (options?.species) params.append('species', options.species);
    if (options?.activeOnly !== undefined) params.append('active_only', String(options.activeOnly));
    
    const queryString = params.toString();
    const url = `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-templates${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to fetch templates' }));
        throw new Error(error.detail || 'Failed to fetch templates');
    }

    return response.json();
}

export async function getPracticeTemplate(
    practiceId: string,
    templateId: string
): Promise<PracticeTemplate> {
    const url = `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-templates/${templateId}`;
    
    const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to fetch template' }));
        throw new Error(error.detail || 'Failed to fetch template');
    }

    return response.json();
}

export async function createPracticeTemplate(
    practiceId: string,
    data: CreatePracticeTemplate
): Promise<PracticeTemplate> {
    const url = `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-templates`;
    
    const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to create template' }));
        throw new Error(error.detail || 'Failed to create template');
    }

    return response.json();
}

export async function updatePracticeTemplate(
    practiceId: string,
    templateId: string,
    data: UpdatePracticeTemplate
): Promise<PracticeTemplate> {
    const url = `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-templates/${templateId}`;
    
    const response = await fetch(url, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to update template' }));
        throw new Error(error.detail || 'Failed to update template');
    }

    return response.json();
}

export async function deletePracticeTemplate(
    practiceId: string,
    templateId: string
): Promise<void> {
    const url = `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-templates/${templateId}`;
    
    const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to delete template' }));
        throw new Error(error.detail || 'Failed to delete template');
    }
}

// ===== TEMPLATE ITEM CRUD =====

export async function addTemplateItem(
    practiceId: string,
    templateId: string,
    data: CreatePracticeTemplateItem
): Promise<PracticeTemplateItem> {
    const url = `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-templates/${templateId}/items`;
    
    const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to add item' }));
        throw new Error(error.detail || 'Failed to add item');
    }

    return response.json();
}

export async function updateTemplateItem(
    practiceId: string,
    itemId: string,
    data: UpdatePracticeTemplateItem
): Promise<PracticeTemplateItem> {
    const url = `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-template-items/${itemId}`;
    
    const response = await fetch(url, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to update item' }));
        throw new Error(error.detail || 'Failed to update item');
    }

    return response.json();
}

export async function deleteTemplateItem(
    practiceId: string,
    itemId: string
): Promise<void> {
    const url = `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-template-items/${itemId}`;
    
    const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to delete item' }));
        throw new Error(error.detail || 'Failed to delete item');
    }
}

// ===== APPLY TEMPLATE =====

export async function applyPracticeTemplate(
    petId: string,
    request: ApplyPracticeTemplateRequest
): Promise<ImmunizationSchedule[]> {
    const url = `${API_BASE_URL}/api/v1/pets/${petId}/immunization-schedule/apply-practice-template`;
    
    const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to apply template' }));
        throw new Error(error.detail || 'Failed to apply template');
    }

    return response.json();
}


// ===== STANDARD TEMPLATES (IMPORT) =====

/**
 * Get all available standard (system-wide) templates that can be imported.
 * These are read-only templates that practices can copy and customize.
 */
export async function getStandardTemplates(
    species?: string
): Promise<StandardTemplate[]> {
    const params = new URLSearchParams();
    if (species) params.append('species', species);
    
    const queryString = params.toString();
    const url = `${API_BASE_URL}/api/v1/standard-immunization-templates${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to fetch standard templates' }));
        throw new Error(error.detail || 'Failed to fetch standard templates');
    }

    return response.json();
}

/**
 * Import a standard template into the practice's custom templates.
 * Creates a practice-owned copy that can be edited and customized.
 */
export async function importStandardTemplate(
    practiceId: string,
    request: ImportStandardTemplateRequest
): Promise<PracticeTemplate> {
    const url = `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-templates/import`;
    
    const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to import template' }));
        throw new Error(error.detail || 'Failed to import template');
    }

    return response.json();
}


// ===== REMINDER TRIGGER CRUD =====

/**
 * Get all reminder triggers for a practice immunization template.
 */
export async function getReminderTriggers(
    templateId: string
): Promise<ReminderTrigger[]> {
    const url = `${API_BASE_URL}/api/v1/practice-templates/${templateId}/reminder-triggers`;
    
    const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to fetch triggers' }));
        throw new Error(error.detail || 'Failed to fetch triggers');
    }

    return response.json();
}

/**
 * Create a new reminder trigger for a practice immunization template.
 */
export async function createReminderTrigger(
    templateId: string,
    data: CreateReminderTrigger
): Promise<ReminderTrigger> {
    const url = `${API_BASE_URL}/api/v1/practice-templates/${templateId}/reminder-triggers`;
    
    const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to create trigger' }));
        throw new Error(error.detail || 'Failed to create trigger');
    }

    return response.json();
}

/**
 * Update a reminder trigger.
 */
export async function updateReminderTrigger(
    templateId: string,
    triggerId: string,
    data: UpdateReminderTrigger
): Promise<ReminderTrigger> {
    const url = `${API_BASE_URL}/api/v1/practice-templates/${templateId}/reminder-triggers/${triggerId}`;
    
    const response = await fetch(url, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to update trigger' }));
        throw new Error(error.detail || 'Failed to update trigger');
    }

    return response.json();
}

/**
 * Delete a reminder trigger.
 */
export async function deleteReminderTrigger(
    templateId: string,
    triggerId: string
): Promise<void> {
    const url = `${API_BASE_URL}/api/v1/practice-templates/${templateId}/reminder-triggers/${triggerId}`;
    
    const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to delete trigger' }));
        throw new Error(error.detail || 'Failed to delete trigger');
    }
}
